import React, { useState, useEffect } from "react";
import { object, number, func, oneOfType, string, bool } from "prop-types";
import { Row, Col, Button, Modal } from "react-bootstrap";
import cloneDeep from "lodash/cloneDeep";
// eslint-disable-next-line import/no-unresolved
import RatingsQuestion from "./RatingsQuestion";

const RateCandidate = ({
  candidateId,
  candidateName,
  userCandidateRating,
  ratingSystem,
  closeModal,
  onRateCandidate,
  onEditCandidateRating,
  candidateStageId,
  candidatesRatings,
  toggleModalView,
  internal,
  postRateCandidatesLoading,
  postRateCandidatesError,
}) => {
  const [ratings, setRatings] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (ratingSystem.size > 0) {
      const newRatings = {};
      if (ratingSystem.get("questions")) {
        ratingSystem
          .get("questions")
          .get("data")
          .forEach((questionObj) => {
            if (userCandidateRating !== false) {
              const ratedValue = userCandidateRating.get(
                `rating_${questionObj.get("ratingQuestionNumber")}`
              );
              const ratedComment = userCandidateRating.get(
                `comment_${questionObj.get("ratingQuestionNumber")}`
              );
              newRatings[questionObj.get("ratingQuestionNumber")] = {
                value: ratedValue,
                comment: ratedComment,
              };
            } else {
              newRatings[questionObj.get("ratingQuestionNumber")] = {
                value: null,
                comment: null,
              };
            }
          });
      }
      if (userCandidateRating !== false) {
        const ratedValue = userCandidateRating.get("rating_overall");
        const ratedComment = userCandidateRating.get("comment_overall");
        newRatings.rating_overall = {
          value: ratedValue,
          comment: ratedComment,
        };
      } else {
        newRatings.rating_overall = { value: null, comment: null };
      }
      setRatings(newRatings);
    }
  }, []);

  const handleStarClick = (questionNumber, value) => {
    const newRatings = cloneDeep(ratings);
    newRatings[questionNumber].value = value;
    setRatings(newRatings);
  };

  const handleCommentInput = (questionNumber, input) => {
    const newRatings = cloneDeep(ratings);
    newRatings[questionNumber].comment = input;
    setRatings(newRatings);
  };

  const handleSaveRating = () => {
    // eslint-disable-next-line no-alert
    if (ratings.rating_overall.value === null) {
      return alert("You must give the candidate an overall rating");
    }
    const dataToSend = {
      testTakerId: candidateId,
      ratingId: ratingSystem.get("ratingId"),
    };
    Object.keys(ratings).forEach((questionNumber) => {
      if (questionNumber === "rating_overall") {
        return null;
      }
      const { value, comment } = ratings[questionNumber];
      if (value !== null) {
        dataToSend[`rating_${questionNumber}`] = value;
      }
      if (comment !== null) {
        dataToSend[`comment_${questionNumber}`] = comment;
      }
    });
    dataToSend.rating_overall = ratings.rating_overall.value || 0;
    if (ratings.rating_overall.comment !== null) {
      dataToSend.comment_overall = ratings.rating_overall.comment;
    }

    if (userCandidateRating === false) {
      onRateCandidate(dataToSend, candidateStageId, internal);
    } else {
      onEditCandidateRating(dataToSend, candidateStageId);
    }
    // We need to modify this function to only call closeModal when the rating has been submitted and is successful.
    setFormSubmitted(true);
  };

  const handleCancel = () => {
    if (
      candidatesRatings.get(String(candidateId)) &&
      candidatesRatings.get(String(candidateId)).size > 0
    ) {
      toggleModalView();
    } else {
      closeModal();
    }
  };
  // We will want to clear out the note input once the note request has been completed.
  useEffect(() => {
    if (
      formSubmitted &&
      !postRateCandidatesLoading &&
      !postRateCandidatesError
    ) {
      closeModal();
      setFormSubmitted(false);
    }
  }, [postRateCandidatesLoading]);

  const RatingQuestions = [];
  if (Object.keys(ratings).length > 0) {
    if (ratingSystem.get("questions")) {
      const questionsData = ratingSystem.get("questions").get("data");
      questionsData.forEach((questionObj) => {
        RatingQuestions.push(
          <RatingsQuestion
            ratingType={ratingSystem.get("ratingType")}
            questionNumber={questionObj.get("ratingQuestionNumber")}
            questionPrompt={questionObj.get("ratingPrompt")}
            state={ratings[questionObj.get("ratingQuestionNumber")]}
            handleStarClick={handleStarClick}
            handleCommentInput={handleCommentInput}
            key={questionObj.get("ratings_questions_id")}
            postRateCandidatesLoading={postRateCandidatesLoading}
          />
        );
      });
    }

    RatingQuestions.push(
      <RatingsQuestion
        ratingType={ratingSystem.get("ratingType")}
        questionNumber="rating_overall"
        questionPrompt="Overall Rating"
        state={ratings.rating_overall}
        handleStarClick={handleStarClick}
        handleCommentInput={handleCommentInput}
        key="rating_overall"
        postRateCandidatesLoading={postRateCandidatesLoading}
      />
    );
  }

  return (
    <div>
      <Modal.Header>
        <h4>Rate Candidate: {candidateName}</h4>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <strong>{ratingSystem.get("ratingName")}</strong>
          </Col>
        </Row>
        <Row>
          <Col lg={12} md={12} sm={12}>
            {RatingQuestions}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleCancel}
          variant="outline-primary"
          className="btn-cancel"
          id="cancelRateCandidate"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveRating}
          id="saveRateCandidate"
          style={{ cursor: postRateCandidatesLoading ? "wait" : "pointer" }}
          disabled={postRateCandidatesLoading}
        >
          Save Ratings
        </Button>
      </Modal.Footer>
    </div>
  );
};

RateCandidate.propTypes = {
  candidateName: string,
  candidateId: oneOfType([string, bool]),
  candidateStageId: oneOfType([string, number]),
  candidatesRatings: object,
  ratingSystem: object,
  closeModal: func,
  toggleModalView: func,
  onRateCandidate: func,
  onEditCandidateRating: func,
  userCandidateRating: object,
  internal: number,
  postRateCandidatesLoading: bool,
  postRateCandidatesError: bool,
};

export default RateCandidate;
